/**
 * OwnerAccountStep — reusable inline signup block used across Arena, Pro and
 * Brand onboarding flows so every "listed on FitFrik" path has zero-friction,
 * consistent account creation.
 *
 * Props:
 *   theme         — "arena" (gold) | "pro" (cyan) | "brand" (orange)
 *   subtitle      — one-liner shown under the "Create your login now" heading
 *   onDone(user)  — called after successful signup with the returned user obj
 *   emailDefault  — pre-fill the email input
 *   nameDefault   — pre-fill the name input
 *
 * Uses the shared auth store `register()` so downstream flows are logged-in
 * immediately, no page reload.
 */
import { useState } from "react";
import { Mail, Lock, Eye, EyeOff, User as UserIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "../../store/authStore";
import { toast } from "sonner";

const THEMES = {
  arena: { bg: "linear-gradient(135deg, rgba(245,197,24,0.15) 0%, rgba(245,197,24,0.06) 100%)", border: "rgba(245,197,24,0.35)", accent: "#B58A00", cta: "#F5C518", ctaFg: "#0C0C0B" },
  pro:   { bg: "linear-gradient(135deg, rgba(0,212,255,0.13) 0%, rgba(0,212,255,0.04) 100%)", border: "rgba(0,212,255,0.35)", accent: "#00B0D5", cta: "#00D4FF", ctaFg: "#0C0C0B" },
  brand: { bg: "linear-gradient(135deg, rgba(255,120,73,0.14) 0%, rgba(255,120,73,0.05) 100%)", border: "rgba(216,88,37,0.35)", accent: "#D85825", cta: "#FF7849", ctaFg: "#0C0C0B" },
};

const fieldLabel = { display: "block", fontFamily: "Barlow", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#8A8884", marginBottom: 4 };
const inputStyle = { width: "100%", height: 44, padding: "0 14px", background: "#E4E2DE", border: "1px solid rgba(12,12,11,0.10)", borderRadius: 12, fontFamily: "Barlow", fontSize: 14, outline: "none", color: "#0C0C0B" };

export default function OwnerAccountStep({ theme = "arena", stepLabel, subtitle, onDone, emailDefault = "", nameDefault = "", ctaLabel = "Create account & continue" }) {
  const navigate = useNavigate();
  const register = useAuthStore((s) => s.register);

  const [name, setName] = useState(nameDefault);
  const [email, setEmail] = useState(emailDefault);
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [consentAge, setConsentAge] = useState(false);
  const [consentTerms, setConsentTerms] = useState(false);
  const [busy, setBusy] = useState(false);

  const t = THEMES[theme] || THEMES.arena;

  const ok = name.trim().length >= 2 && /.+@.+\..+/.test(email) && password.length >= 8 && password === confirm && consentAge && consentTerms;

  const strength = (() => {
    let s = 0;
    if (password.length >= 8) s++;
    if (/[A-Z]/.test(password)) s++;
    if (/[0-9]/.test(password)) s++;
    if (/[^A-Za-z0-9]/.test(password)) s++;
    return s;
  })();
  const strengthLabel = ["Too short", "Weak", "Fair", "Strong", "Excellent"][Math.min(4, strength)];

  const submit = async () => {
    if (!ok) return;
    setBusy(true);
    try {
      const u = await register(name, email, password, {
        age_confirmed: consentAge,
        terms_accepted: consentTerms,
        marketing_consent: false,
      });
      toast.success("Account created");
      onDone?.({ ...u, phone });
    } catch (e) {
      toast.error(e?.message || "Sign-up failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ display: "grid", gap: 14 }} data-testid="owner-account-step">
      <div style={{ background: t.bg, border: `1px solid ${t.border}`, borderRadius: 14, padding: "12px 14px" }}>
        <div style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: t.accent }}>
          {stepLabel || "Owner account"}
        </div>
        <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 800, fontSize: 22, color: "#1A1A1A", marginTop: 4, lineHeight: 1.15 }}>
          Create your login now
        </div>
        <div style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 12, color: "#4A4845", marginTop: 4, lineHeight: 1.5 }}>
          {subtitle || "You’ll use the same email + password to sign in at fitfrik.com/login once your application is approved."}
        </div>
      </div>

      <div>
        <label style={fieldLabel}>Your full name *</label>
        <div style={{ position: "relative" }}>
          <UserIcon size={14} style={{ position: "absolute", left: 14, top: 15, color: "#8A8884" }} />
          <input style={{ ...inputStyle, paddingLeft: 36 }} value={name} onChange={(e) => setName(e.target.value)} data-testid="oa-name" autoComplete="name" />
        </div>
      </div>

      <div>
        <label style={fieldLabel}>Email *</label>
        <div style={{ position: "relative" }}>
          <Mail size={14} style={{ position: "absolute", left: 14, top: 15, color: "#8A8884" }} />
          <input type="email" style={{ ...inputStyle, paddingLeft: 36 }} value={email} onChange={(e) => setEmail(e.target.value)} data-testid="oa-email" autoComplete="email" />
        </div>
      </div>

      <div>
        <label style={fieldLabel}>Phone (WhatsApp)</label>
        <input type="tel" style={inputStyle} value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="oa-phone" autoComplete="tel" placeholder="+91" />
      </div>

      <div>
        <label style={fieldLabel}>Create password *</label>
        <div style={{ position: "relative" }}>
          <Lock size={14} style={{ position: "absolute", left: 14, top: 15, color: "#8A8884" }} />
          <input type={showPw ? "text" : "password"} style={{ ...inputStyle, paddingLeft: 36, paddingRight: 40 }} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="oa-password" autoComplete="new-password" placeholder="At least 8 characters" />
          <button type="button" onClick={() => setShowPw((s) => !s)} aria-label={showPw ? "Hide password" : "Show password"} style={{ position: "absolute", right: 8, top: 8, width: 28, height: 28, background: "transparent", border: 0, display: "grid", placeItems: "center" }}>
            {showPw ? <EyeOff size={14} color="#8A8884" /> : <Eye size={14} color="#8A8884" />}
          </button>
        </div>
        {password && (
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6 }}>
            <div style={{ flex: 1, height: 4, background: "#E7E5E1", borderRadius: 100, overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${(Math.max(1, strength) / 4) * 100}%`, background: strength >= 3 ? "#0FB5A6" : strength === 2 ? "#F5C518" : "#E5484D", transition: "width 180ms ease" }} />
            </div>
            <span style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 10, color: "#8A8884", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" }}>{strengthLabel}</span>
          </div>
        )}
      </div>

      <div>
        <label style={fieldLabel}>Confirm password *</label>
        <input type={showPw ? "text" : "password"} style={inputStyle} value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="oa-password-confirm" autoComplete="new-password" />
      </div>

      <label style={{ display: "flex", gap: 8, alignItems: "flex-start", cursor: "pointer" }}>
        <input type="checkbox" checked={consentAge} onChange={(e) => setConsentAge(e.target.checked)} data-testid="oa-consent-age" style={{ marginTop: 3 }} />
        <span style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 12, color: "#4A4845", lineHeight: 1.5 }}>
          I confirm I am 18 years or older.
        </span>
      </label>

      <label style={{ display: "flex", gap: 8, alignItems: "flex-start", cursor: "pointer" }}>
        <input type="checkbox" checked={consentTerms} onChange={(e) => setConsentTerms(e.target.checked)} data-testid="oa-consent-terms" style={{ marginTop: 3 }} />
        <span style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 12, color: "#4A4845", lineHeight: 1.5 }}>
          I accept the FitFrik <b>Terms</b> and <b>Privacy Policy</b>.
        </span>
      </label>

      <button
        onClick={submit}
        disabled={!ok || busy}
        data-testid="oa-cta"
        style={{
          height: 52, marginTop: 4,
          background: ok && !busy ? t.cta : "#D8D6D1",
          color: ok && !busy ? t.ctaFg : "#8A8884",
          border: "none", borderRadius: 100,
          fontFamily: "Barlow", fontSize: 14, fontWeight: 800,
          cursor: ok && !busy ? "pointer" : "not-allowed",
          boxShadow: ok && !busy ? `0 6px 18px ${t.cta}55` : "none",
        }}
      >
        {busy ? "Creating account…" : ctaLabel}
      </button>

      <div style={{ textAlign: "center", marginTop: -4 }}>
        <button type="button" onClick={() => navigate(`/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`)} data-testid="oa-existing-login" style={{ background: "transparent", border: 0, color: "#4A4845", fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
          Already have a FitFrik account? <span style={{ color: t.accent, fontWeight: 700 }}>Sign in</span>
        </button>
      </div>
    </div>
  );
}
